import "./form-contract.scss";

import { GetWaiverDto } from "@api/api";
import useAutocompletePatient from "@domain/patient/autocomplete-patient/autocomplete-patient";
import useAutocompletePerson from "@domain/person/autocomplete-person/autocomplete-person";
import { ContractFormSchema } from "@factory/form/form.rule";
import { GetUtilServiceModel } from "@factory/model/query-model-util-service";
import { useQyGetUtilService } from "@factory/query/util-service.query";
import { useQyGetWaiver } from "@factory/query/waiver.query";
import { LabelValue } from "@shared/models/label-value.interface";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import InputDigitControl from "@shared/ui/hook-form/input-digit-control/input-digit-control";
import InlineBlock from "@shared/ui/inline-block/inline-block";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { base64ToFile } from "@shared/utility/convert-base64-to-file";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { numberFormat } from "@shared/utility/formats/number-format";
import { getWaiverCheckDefaults } from "@shared/utility/waiver-checkbox";
import { Button } from "primereact/button";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import ReactSignatureCanvas from "react-signature-canvas";

type InputFormSchema = ContractFormSchema;
export interface FormContractProps {
  isNew?: boolean;
  signatureBlob?: string | null;
  handleWaiverPreview?: (value: GetWaiverDto) => void;
}

type ContractOutletContext = {
  signatureBlob: string | null;
  handleWaiverPreview: (value: GetWaiverDto) => void;
};

export function FormContract({
  isNew,
  signatureBlob: propSignatureBlob,
  handleWaiverPreview: propHandleWaiverPreview,
}: FormContractProps) {
  const { control, setValue, watch } = useFormContext<ContractFormSchema>();
  const { showError } = useNotificationContext();
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const sigCanvas = useRef<any>();
  const sessionValue = watch("session");
  const amountValue = watch("amount");
  const signatureValue = watch("signature");
  const patientName = watch("patientName");
  const serviceName = watch("serviceName");
  const doctorName = watch("personName");
  const waiver = watch("waiver");

  const context = useOutletContext<ContractOutletContext | null>();
  const signatureBlob = propSignatureBlob ?? context?.signatureBlob ?? null;
  const handleWaiverPreview =
    propHandleWaiverPreview ?? context?.handleWaiverPreview;

  const { data: responseService } = useQyGetUtilService("", 9999);
  const serviceOptions = (responseService?.data || []).map(
    (item) => new LabelValue(item.name, item.code, item)
  );

  const { data: responseWaiver } = useQyGetWaiver("", 9999);
  const waiverOptions = (responseWaiver?.data ?? []).map(
    (item) => new LabelValue(item.name, item.code, item)
  );

  const shouldShowSignature = useCallback(() => {
    if (isNew) {
      return true;
    }

    if (!isNew && !signatureValue) {
      return true;
    }

    return false;
  }, [signatureValue, isNew]);

  useEffect(() => {
    if (signatureBlob && sigCanvas.current) {
      sigCanvas.current.fromDataURL(signatureBlob);
    }
  }, [signatureBlob]);

  const handleSelect = (selected: LabelValue) => {
    if (!selected) {
      return;
    }

    setValue("patient", selected.value);
    setValue("patientName", selected.label);
  };
  const { formElement } = useAutocompletePatient(
    null,
    "patient",
    "Patient",
    "Enter and find the name of the patient",
    handleSelect
  );

  const handleServiceSelect = (selected: string) => {
    const selectedServiceModel = serviceOptions.find(
      (item) => item.value === selected
    );
    const selectedServiceLabelVal =
      selectedServiceModel?.payload as GetUtilServiceModel;

    if (!selectedServiceLabelVal) {
      return;
    }

    setValue("amount", selectedServiceLabelVal.amount ?? 0);
    setValue("service", selectedServiceLabelVal.code);
    setValue("serviceName", selectedServiceLabelVal.name);
  };

  // A newly picked waiver starts from its author's defaults; if it has any
  // checkboxes, open it straight away so they can be ticked.
  const handleWaiverSelect = (selected: string) => {
    const selectedWaiver = waiverOptions.find(
      (item) => item.value === selected
    )?.payload as GetWaiverDto | undefined;

    if (!selectedWaiver) {
      return;
    }

    const defaults = getWaiverCheckDefaults(selectedWaiver.description);
    setValue("waiverChecks", defaults);

    if (defaults.length > 0 && handleWaiverPreview) {
      handleWaiverPreview(selectedWaiver);
    }
  };

  const handleSelectPerson = (selected: LabelValue) => {
    if (!selected) {
      return;
    }

    setValue("person", selected.value);
    setValue("personName", selected.label);
  };
  const { formElement: formElementDoctor } = useAutocompletePerson(
    null,
    "doctor",
    "Doctor",
    "",
    true,
    handleSelectPerson
  );

  const handleSaveSignature = () => {
    if (sigCanvas.current?.isEmpty()) {
      showError("Please provide a signature before saving.");
      return;
    }

    const signatureImg = sigCanvas.current?.getCanvas().toDataURL("image/png");

    if (signatureImg) {
      const signatureFile = base64ToFile(signatureImg, "signature.png");

      setValue("file", signatureFile);

      const previewUrl = URL.createObjectURL(signatureFile);
      setSignaturePreview(previewUrl);
    }
  };

  const handleClearSignature = () => {
    sigCanvas.current.clear();
    setSignaturePreview(null);
    setValue("file", null);
  };

  return (
    <div id="FormContract" className="contract">
      <section className="grid md:grid-cols-2 gap-4">
        <div className="flex flex-col flex-wrap gap-1">
          <InlineBlock
            disabled={!isNew}
            label="Patient Name"
            value={patientName}
            hint="Click to select Patient"
          >
            {formElement}
          </InlineBlock>

          <InlineBlock
            disabled={!isNew}
            label="Service Name"
            value={serviceName}
            hint="Click to select Service"
          >
            <DropdownControl<InputFormSchema>
              control={control}
              name="service"
              label="Service"
              options={serviceOptions}
              className="w-full md:w-full"
              containerClassName="pb-2"
              placeholder="Enter contract"
              onSelect={handleServiceSelect}
              filter={true}
            />
          </InlineBlock>

          <InlineBlock
            disabled={!isNew}
            label="Amount"
            value={amountValue ? currencyFormat(amountValue, "PHP") : undefined}
            hint="Click to select amount"
          >
            <InputDigitControl<InputFormSchema>
              control={control}
              name="amount"
              label="Amount"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              placeholder="Enter amount"
            />
          </InlineBlock>

          <InlineBlock
            disabled={!isNew}
            label="Session"
            value={sessionValue ? numberFormat(sessionValue) : undefined}
            hint="Click to select session"
          >
            <InputDigitControl<InputFormSchema>
              control={control}
              name="session"
              label="Session"
              className="w-full md:w-3/4"
              containerClassName="pb-2"
              placeholder="Enter sessions"
            />
          </InlineBlock>

          <InlineBlock
            disabled={!isNew}
            label="Doctor"
            value={doctorName}
            hint="Click to select doctor"
          >
            {formElementDoctor}
          </InlineBlock>
        </div>

        <section className="flex flex-col gap-2">
          <div className="flex gap-2 items-center">
            <DropdownControl<InputFormSchema>
              control={control}
              name="waiver"
              label="Waiver"
              options={waiverOptions}
              className="w-full"
              containerClassName="pb-2 block w-full"
              placeholder="Enter waiver"
              onSelect={handleWaiverSelect}
            />
            <Button
              className="block h-12 w-32 mt-3"
              label="Preview"
              severity="secondary"
              outlined
              onClick={() => {
                if (handleWaiverPreview && waiver) {
                  const selectedWaiver = waiverOptions.find(
                    (item) => item.value === waiver
                  )?.payload;
                  handleWaiverPreview(selectedWaiver);
                }
              }}
            />
          </div>
          <div>
            {shouldShowSignature() ? (
              <section className="flex flex-col justify-center">
                <label>Signature</label>
                <div className="signature-wrapper">
                  <ReactSignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    canvasProps={{
                      width: 400,
                      height: 200,
                      className: "signature-canvas border border-gray-300",
                    }}
                    minWidth={2}
                    maxWidth={4}
                  />
                  <div className="flex gap-2 mt-3">
                    <Button label="Clear" onClick={handleClearSignature} />
                    <Button
                      label="Save Signature"
                      onClick={handleSaveSignature}
                    />
                  </div>
                </div>

                <section>
                  {signaturePreview && (
                    <div className="signature-preview mt-5">
                      <img
                        src={signaturePreview}
                        alt="Saved Signature"
                        style={{
                          width: "150px",
                          height: "100px",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}
                </section>
              </section>
            ) : null}
          </div>
        </section>
      </section>
    </div>
  );
}

export default FormContract;
